import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Row, Col, Button, Input, Modal, Skeleton, Tabs, Space, DatePicker } from "antd";
import dayjs from "dayjs";
import Select from "react-select";
import WindowedSelect from "react-windowed-select";
import { toInteger } from "lodash";

const timer_rounding_intervals = [
  { label: "Whole Hour", value: "hour", interval: 60 },
  { label: "Half Hour", value: "half-hour", interval: 30 },
  { label: "Quarter Hour", value: "quarter-hour", interval: 15 },
  { label: "Tenth Hour", value: "tenth-hour", interval: 6 },
  { label: "To The Minute", value: "no rounding", interval: 1 },
];

let BASE_STATE_TIME_ENTRY = {
  client: null,
  clients_xref: null,
  job: null,
  jobs_xref: null,
  task: null,
  tasks_xref: null,
  description: null,
  comments: "",
  item: null,
  items_xref: null,
  task_status: null,
  task_statuses_xref: null,
  due_at: null,
  form_due_date: null,
  jobs_xref: null,
  owned_by: null,
  time_at: dayjs(),
};

function ModalAddTimeEntry(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(1200);

  const [timeEntry, setTimeEntry] = useState(BASE_STATE_TIME_ENTRY);

  const [companySettings, setCompanySettings] = useState({});
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [users, setUsers] = useState([]);
  const [billRates, setBillRates] = useState([]);
  const [items, setItems] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [billingStatuses, setBillingStatuses] = useState([]);

  const focusFirst = useRef(null); // to be removed
  const refClientSelector = useRef(null);
  const refJobSelector = useRef(null);
  const refTaskSelector = useRef(null);
  const refTimeBySelector = useRef(null);
  const refItemSelector = useRef(null);
  const refBillingStatusSelector = useRef(null);
  const refCommentsInput = useRef(null);
  const refDateofWorkInput = useRef(null);
  const refHoursInput = useRef(null);
  const refTaskStatusSelector = useRef(null);
  const refAddTimeButton = useRef(null);

  const GetCompanyPreferences = async () => {
    let response = await props.AxiosRequest("/api/companies/get");
    setCompanySettings(response.data.company);
  };

  const GetClients = async () => {
    let response = await props.AxiosRequest("/api/clients/get/all");
    let mapped_clients = response.data.clients
      .map((m) => {
        return { ...m, value: m.id, label: m.name };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    setClients(mapped_clients);
  };
  const GetJobs = async () => {
    if (!timeEntry?.clients_xref) return;
    let response = await props.AxiosRequest("/api/jobs/get", { client_id: timeEntry?.clients_xref });
    let mapped_jobs = response.data.jobs
      .map((m) => {
        return { ...m, value: m.id, label: m.name };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    setJobs(mapped_jobs);
  };
  const GetTasks = async () => {
    if (!timeEntry?.clients_xref && !timeEntry?.jobs_xref) return;
    let response = await props.AxiosRequest("/api/tasks/get", { clients_xref: timeEntry?.clients_xref, jobs_xref: timeEntry?.jobs_xref });
    let mapped_tasks = response.data.tasks
      .map((m) => {
        return { ...m, value: m.id, label: m.description };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    setTasks(mapped_tasks);
  };

  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get");
    let statuses = response.data.task_statuses
      .map((m) => {
        return { ...m, label: m.name, value: m.id };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    setTaskStatuses(statuses);
  };

  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get");
    let mapped_users = response.data.users
      .map((m) => {
        return { ...m, value: m.id, label: m.name };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    // let default_user = mapped_users.find((f) => f.current_user === 1);
    setUsers(mapped_users);
    // if (!timeEntry?.time_by) {
    //   setTimeEntry({ ...timeEntry, time_by: default_user.value, user: default_user });
    // }
  };

  const GetBillRates = async () => {
    let response = await props.AxiosRequest("/api/bill-rates/get");
    let mapped_rates = response.data.bill_rates.map((m) => {
      return { ...m, value: m.id, label: m.description };
    });
    setBillRates(mapped_rates);
  };

  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    let mapped_items = response.data.items
      .map((m) => {
        return { ...m, value: m.id, label: m.name };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    setItems(mapped_items);
  };

  const GetBillingStatuses = async () => {
    let response = await props.AxiosRequest("/api/billing-statuses/get");
    let mapped_billing_statuses = response.data.billing_statuses
      .map((m) => {
        return { ...m, value: m.id, label: m.name };
      })
      .sort((a, b) => (a.label > b.label ? 1 : 0));
    setBillingStatuses(mapped_billing_statuses);
  };

  const AddTimeEntry = async () => {
    if (
      !timeEntry.clients_xref ||
      !timeEntry.jobs_xref ||
      !timeEntry.tasks_xref ||
      !timeEntry.time_by ||
      !timeEntry.items_xref ||
      !timeEntry.billing_statuses_xref ||
      !timeEntry.time_at ||
      !timeEntry.hours ||
      !timeEntry.task_statuses_xref
    )
      return props.MakeNotification("info", "Please fill in all required fields.");
    await props.AxiosRequest("/api/time-entries/insert", { ...timeEntry });
    props.setAddTimeEntryProps({ ...props.addTimeEntryProps, open: false, refresh: true });
    ResetModalToDefaultState();
  };

  const CalculateBillRate = async () => {
    let calculated_bill_rate = 0;
    let bill_rate = 0;
    if (timeEntry?.user?.billing_groups_xref && timeEntry?.user && timeEntry?.items_xref) {
      let found_bill_rate = billRates.find((f) => f.billing_groups_xref === timeEntry?.user?.billing_groups_xref && f.items_xref === timeEntry?.items_xref);
      if (found_bill_rate) {
        calculated_bill_rate = found_bill_rate.bill_rate * timeEntry.hours;
        bill_rate = found_bill_rate?.bill_rate;
      }
    }
    setTimeEntry({ ...timeEntry, extended_rate: calculated_bill_rate, bill_rate: bill_rate });
  };

  const SetDefaultsFromProps = async () => {
    if (!clients || !jobs || !tasks) return;
    let found_client = null,
      found_job = null,
      found_task = null,
      found_user = null;
    let time_at = null;
    let rounded_hours = 0;
    let found_comments = "";
    if (props?.addTimeEntryProps?.default_date) time_at = props?.addTimeEntryProps?.default_date;
    if (props?.addTimeEntryProps?.client_id) found_client = clients.find((f) => f.id === props?.addTimeEntryProps?.client_id);
    if (props?.addTimeEntryProps?.job_id) found_job = jobs.find((f) => f.id === props?.addTimeEntryProps?.job_id);
    if (props?.addTimeEntryProps?.task_id) found_task = tasks.find((f) => f.id === props?.addTimeEntryProps?.task_id);
    if (!found_user) found_user = users.find((f) => f.current_user === 1);
    if (props?.addTimeEntryProps?.timer_entries?.length > 0) {
      //hours calculation
      //add seconds_elapsed
      rounded_hours = props?.addTimeEntryProps?.timer_entries.reduce((a, b) => a + Number(b.seconds_elapsed), 0);
      // convert to minutes
      rounded_hours = rounded_hours / 60.0;
      // round to closest interval

      if (companySettings?.rounding_interval) {
        let rounding_interval = timer_rounding_intervals?.find((f) => f.value === companySettings?.rounding_interval)?.interval;
        console.log(Math.max(rounded_hours / Number(rounding_interval * 1.0), 1));
        rounded_hours = Math.round(Math.max(rounded_hours / Number(rounding_interval * 1.0), 1)) * (rounding_interval / 60.0);
      }
      // comments condensing
      props?.addTimeEntryProps?.timer_entries?.forEach((a) => {
        if (a.comments) found_comments += a.comments + "\n";
      });
    }
    setTimeEntry({
      ...timeEntry,
      client: found_client,
      clients_xref: found_client?.value,
      job: found_job,
      jobs_xref: found_job?.value,
      task: found_task,
      hours: rounded_hours || "",
      tasks_xref: found_task?.value,
      time_at: time_at ? time_at : dayjs(),
      user: found_user,
      comments: found_comments || "",
      time_by: found_user?.value,
      timer_entries: props?.addTimeEntryProps?.timer_entries,
    });
    if (!found_client) return refClientSelector?.current?.focus();
    if (found_client && !found_job) return refJobSelector?.current?.focus();
    if (found_client && found_job && !found_task) return refTaskSelector?.current?.focus();
    if (found_client && found_job && found_task) return refCommentsInput?.current?.focus();
  };

  const SetDefaultsFromTask = async () => {
    let found_item = null,
      found_billing_status = null,
      found_task_status = null;
    if (timeEntry?.task) found_item = items.find((f) => f.id === timeEntry?.task.items_xref);
    if (found_item) found_billing_status = billingStatuses.find((f) => f.id === found_item.billing_statuses_xref);
    if (timeEntry?.task) found_task_status = taskStatuses.find((f) => f.id === timeEntry?.task.task_statuses_xref);
    setTimeEntry({
      ...timeEntry,
      item: found_item,
      items_xref: found_item?.value,
      billing_status: found_billing_status,
      billing_statuses_xref: found_billing_status?.value,
      task_status: found_task_status,
      task_statuses_xref: found_task_status?.value,
    });
  };

  const ResetModalToDefaultState = async () => {
    setTimeEntry(BASE_STATE_TIME_ENTRY);
    setJobs([]);
    setTasks([]);
  };

  useEffect(() => {
    if (!modalOpen && props.addTimeEntryProps.open) {
      GetItems();
      GetJobs();
      GetClients();
      GetCompanyPreferences();
      GetUsers();
      GetBillRates();
      GetTaskStatuses();
      GetBillingStatuses();
      SetDefaultsFromProps();
    }
    setModalOpen(props.addTimeEntryProps.open);
  }, [props.addTimeEntryProps.open, timeEntry.owned_by]);

  useEffect(() => {
    if (timeEntry?.clients_xref) {
      GetJobs();
    }
  }, [timeEntry.clients_xref]);
  useEffect(() => {
    if (timeEntry?.jobs_xref) GetTasks();
  }, [timeEntry.jobs_xref]);

  useEffect(() => {
    if (timeEntry?.owned_by && timeEntry.items_xref && billRates) CalculateBillRate();
  }, [timeEntry.owned_by, timeEntry.items_xref, billRates]);

  useEffect(() => {
    if (props?.addTimeEntryProps?.job_id || timeEntry?.jobs_xref) GetTasks();
  }, [props.addTimeEntryProps.job_id, timeEntry.jobs_xref]);

  useEffect(() => {
    if (props.addTimeEntryProps.open)
      if (
        (!timeEntry?.clients_xref && props?.addTimeEntryProps?.client_id) ||
        (!timeEntry?.jobs_xref && props?.addTimeEntryProps?.job_id) ||
        (!timeEntry?.tasks_xref && props?.addTimeEntryProps?.task_id) ||
        (!timeEntry?.time_by && users)
      ) {
        SetDefaultsFromProps();
      }
  }, [
    props.addTimeEntryProps.open,
    props.addTimeEntryProps.client_id,
    props.addTimeEntryProps.job_id,
    props.addTimeEntryProps.task_id,
    timeEntry?.time_by,
    props.addTimeEntryProps.default_date,
    clients,
    jobs,
    tasks,
    users,
  ]);

  useEffect(() => {
    if (timeEntry?.tasks_xref) SetDefaultsFromTask();
  }, [timeEntry.tasks_xref, billingStatuses, taskStatuses, users]);

  return (
    <>
      <Modal
        afterOpenChange={(open) => open && !timeEntry?.clients_xref && refClientSelector.current?.focus()}
        title="Add A New Time Entry"
        centered
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          ResetModalToDefaultState();
          props.setAddTimeEntryProps({ ...props.addTimeEntryProps, open: false, client_id: null });
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setModalOpen(false);
                ResetModalToDefaultState();
                props.setAddTimeEntryProps({ ...props.addTimeEntryProps, open: false, client_id: null });
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={AddTimeEntry} ref={refAddTimeButton}>
              Add Time Entry
            </Button>
          </>
        }
        width={modalWidth}
      >
        {timeEntry ? (
          <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
            <Col span={8}>
              <label className="col-form-label-sm">Client *</label>
              <WindowedSelect
                value={timeEntry.client}
                options={props.MakeClientOptions(clients)}
                onChange={(e) => {
                  if (e.value !== timeEntry.clients_xref) {
                    setTimeEntry({
                      ...timeEntry,
                      client: e,
                      clients_xref: e.value,
                      job: null,
                      jobs_xref: null,
                      task: null,
                      tasks_xref: null,
                      item: null,
                      items_xref: null,
                      billing_status: null,
                      billing_statuses_xref: null,
                      task_status: null,
                      task_statuses_xref: null,
                    });
                  }
                  refJobSelector?.current?.focus();
                }}
                ref={refClientSelector}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Job *</label>
              <Select
                value={timeEntry.job}
                options={props.MakeJobOptions(jobs)}
                onChange={(e) => {
                  if (e.value !== timeEntry.jobs_xref) {
                    setTimeEntry({ ...timeEntry, job: e, jobs_xref: e.value, task: null, tasks_xref: null, item: null, items_xref: null, billing_status: null, billing_statuses_xref: null });
                  }
                  refTaskSelector?.current?.focus();
                }}
                ref={refJobSelector}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Task *</label>
              <Select
                value={timeEntry.task}
                options={props.MakeTaskOptions(
                  tasks,
                  taskStatuses?.find((f) => f.end === 1),
                )}
                onChange={(e) => {
                  setTimeEntry({ ...timeEntry, task: e, tasks_xref: e.value });
                  refTimeBySelector?.current?.focus();
                }}
                ref={refTaskSelector}
              />
            </Col>
            <Col span={24}>
              <hr />
            </Col>

            <Col span={8}>
              <label className="col-form-label-sm">Time By *</label>
              <Select
                value={timeEntry.user}
                options={props.MakeUserOptions(users)}
                onChange={(e) => {
                  let calculated_bill_rate = 0;
                  let bill_rate = 0;
                  if (timeEntry?.user?.billing_groups_xref && e && timeEntry.item) {
                    let found_bill_rate = billRates.find((f) => f.billing_groups_xref === e.billing_groups_xref && f.items_xref === timeEntry.items_xref);
                    if (found_bill_rate) {
                      calculated_bill_rate = found_bill_rate.bill_rate * timeEntry.hours;
                      bill_rate = found_bill_rate?.bill_rate;
                    }
                  }
                  setTimeEntry({ ...timeEntry, user: e, time_by: e.value, extended_rate: calculated_bill_rate, bill_rate: bill_rate });
                  refItemSelector?.current?.focus();
                }}
                ref={refTimeBySelector}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Item *</label>
              <Select
                value={timeEntry.item}
                options={items}
                onChange={(e) => {
                  let calculated_bill_rate = 0;
                  let bill_rate = 0;
                  if (timeEntry?.user?.billing_groups_xref && e && timeEntry.item) {
                    let found_bill_rate = billRates.find((f) => f.billing_groups_xref === timeEntry.user.billing_groups_xref && f.items_xref === e.value);
                    if (found_bill_rate) {
                      calculated_bill_rate = found_bill_rate.bill_rate * timeEntry.hours;
                      bill_rate = found_bill_rate?.bill_rate;
                    }
                  }
                  let found_billing_status = billingStatuses.find((f) => f.id === e.billing_statuses_xref);
                  setTimeEntry({
                    ...timeEntry,
                    item: e,
                    items_xref: e.value,
                    extended_rate: calculated_bill_rate,
                    bill_rate: bill_rate,
                    billing_status: found_billing_status,
                    billing_statuses_xref: e.billing_statuses_xref,
                  });
                  refTaskStatusSelector?.current?.focus();
                }}
                ref={refItemSelector}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Task Status *</label>
              <Select
                value={timeEntry.task_status}
                options={taskStatuses}
                onChange={(e) => {
                  setTimeEntry({ ...timeEntry, task_status: e, task_statuses_xref: e.value });
                  refCommentsInput?.current?.focus();
                }}
                ref={refTaskStatusSelector}
              />
            </Col>

            <Col span={24}>
              <label className="col-form-label-sm">Comments</label>
              <Input.TextArea
                value={timeEntry.comments}
                onChange={(e) => {
                  setTimeEntry({ ...timeEntry, comments: e.target.value });
                }}
                ref={refCommentsInput}
              />
            </Col>

            <Col span={6}>
              <label className="col-form-label-sm">Date of Work *</label>
              <DatePicker
                format={"MM/DD/YYYY"}
                style={{ width: "100%" }}
                allowClear={false}
                value={timeEntry.time_at}
                onChange={(e) => {
                  setTimeEntry({ ...timeEntry, time_at: e });
                  refHoursInput?.current?.focus();
                }}
                ref={refDateofWorkInput}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Hours *</label>
              <Input
                type="number"
                value={timeEntry.hours}
                onChange={(e) => {
                  let calculated_bill_rate = 0;
                  let bill_rate = 0;
                  if (timeEntry?.user?.billing_groups_xref && e && timeEntry.item) {
                    let found_bill_rate = billRates.find((f) => f.billing_groups_xref === timeEntry.user.billing_groups_xref && f.items_xref === timeEntry.items_xref);
                    if (found_bill_rate) {
                      calculated_bill_rate = found_bill_rate.bill_rate * e.target.value;
                      bill_rate = found_bill_rate.bill_rate;
                    }
                  }
                  setTimeEntry({ ...timeEntry, hours: e.target.value, extended_rate: calculated_bill_rate, bill_rate: bill_rate });
                }}
                ref={refHoursInput}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Billing Status *</label>
              <Select
                type="number"
                options={billingStatuses}
                value={timeEntry.billing_status}
                onChange={(e) => {
                  setTimeEntry({ ...timeEntry, billing_status: e, billing_statuses_xref: e.value });
                  refAddTimeButton?.current?.focus();
                }}
                ref={refBillingStatusSelector}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Bill Rate</label>
              <Input readOnly prefix="$" disabled value={timeEntry.bill_rate} />
            </Col>
          </Row>
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  );
}

export default ModalAddTimeEntry;
